import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, orderBy, query, updateDoc, doc, where, Timestamp, getCountFromServer } from 'firebase/firestore';

type DatasetRequestRow = {
  id: string;
  name?: string;
  email?: string;
  company?: string;
  useCase?: string;
  dataType?: string;
  description?: string;
  timeline?: string;
  budget?: string;
  submittedDate?: string;
  submittedTime?: string;
  createdAt?: Timestamp;
  status?: string;
  notes?: string;
  source?: string;
};

type Stats = {
  last7Days: number;
  last30Days: number;
  lastYear: number;
};

const STATUS_OPTIONS = ['new', 'contacted', 'in_progress', 'closed'] as const;

export default function DatasetRequests() {
  const [rows, setRows] = useState<DatasetRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({ last7Days: 0, last30Days: 0, lastYear: 0 });
  const [savingStatusId, setSavingStatusId] = useState<string | null>(null);
  const [savingNotesId, setSavingNotesId] = useState<string | null>(null);
  const [savingAckId, setSavingAckId] = useState<string | null>(null);
  const [notesDrafts, setNotesDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    document.title = 'Dataset Requests — Pixano';
  }, []);

  useEffect(() => {
    const requestsQuery = query(collection(db, 'datasetRequests'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DatasetRequestRow, 'id'>) }));
        setRows(data);
        setNotesDrafts((prev) => {
          const next = { ...prev };
          for (const item of data) {
            if (next[item.id] === undefined) {
              next[item.id] = item.notes ?? '';
            }
          }
          return next;
        });
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      const oneYearAgo = new Date(now);
      oneYearAgo.setFullYear(now.getFullYear() - 1);

      const [last7Snap, last30Snap, lastYearSnap] = await Promise.all([
        getCountFromServer(
          query(collection(db, 'datasetRequests'), where('createdAt', '>=', Timestamp.fromDate(sevenDaysAgo)))
        ),
        getCountFromServer(
          query(collection(db, 'datasetRequests'), where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)))
        ),
        getCountFromServer(
          query(collection(db, 'datasetRequests'), where('createdAt', '>=', Timestamp.fromDate(oneYearAgo)))
        ),
      ]);

      setStats({
        last7Days: last7Snap.data().count,
        last30Days: last30Snap.data().count,
        lastYear: lastYearSnap.data().count,
      });
    };

    loadStats().catch(() => {
      setStats({ last7Days: 0, last30Days: 0, lastYear: 0 });
    });
  }, [rows]);

  const displayedRows = useMemo(() => {
    const active = rows.filter((row) => row.status !== 'acknowledged');
    const acknowledged = rows.filter((row) => row.status === 'acknowledged');
    return [...active, ...acknowledged];
  }, [rows]);

  const handleStatusChange = async (id: string, status: string) => {
    setSavingStatusId(id);
    try {
      await updateDoc(doc(db, 'datasetRequests', id), { status });
    } finally {
      setSavingStatusId(null);
    }
  };

  const handleNotesBlur = async (id: string) => {
    setSavingNotesId(id);
    try {
      await updateDoc(doc(db, 'datasetRequests', id), { notes: notesDrafts[id] ?? '' });
    } finally {
      setSavingNotesId(null);
    }
  };

  const handleAcknowledge = async (id: string) => {
    setSavingAckId(id);
    try {
      await updateDoc(doc(db, 'datasetRequests', id), { status: 'acknowledged' });
    } finally {
      setSavingAckId(null);
    }
  };

  return (
    <div>
      <div className="border-b mb-4 pb-2">
        <h1 className="text-3xl font-bold mb-4 text-gray-900">Dataset Requests</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded border bg-white p-4">
          <div className="text-sm text-gray-600">Last 7 Days</div>
          <div className="text-2xl font-semibold mt-2 text-gray-900">{stats.last7Days}</div>
        </div>
        <div className="rounded border bg-white p-4">
          <div className="text-sm text-gray-600">Last 30 Days</div>
          <div className="text-2xl font-semibold mt-2 text-gray-900">{stats.last30Days}</div>
        </div>
        <div className="rounded border bg-white p-4">
          <div className="text-sm text-gray-600">Last Year</div>
          <div className="text-2xl font-semibold mt-2 text-gray-900">{stats.lastYear}</div>
        </div>
      </div>

      <div className="rounded border bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 border-b">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Time</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Company</th>
              <th className="px-3 py-2 text-left">Use Case</th>
              <th className="px-3 py-2 text-left">Data Type</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Timeline</th>
              <th className="px-3 py-2 text-left">Budget</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Notes</th>
              <th className="px-3 py-2 text-left">Acknowledge</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-gray-500">
                  Loading dataset requests...
                </td>
              </tr>
            ) : displayedRows.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-gray-500">
                  No dataset requests found.
                </td>
              </tr>
            ) : (
              displayedRows.map((row) => {
                const isAcknowledged = row.status === 'acknowledged';
                return (
                  <tr key={row.id} className={`border-t border-gray-200 align-top ${isAcknowledged ? 'opacity-50 bg-gray-50' : ''}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.submittedDate || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.submittedTime || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-900">{row.name || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.email || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.company || '-'}</td>
                    <td className="px-3 py-2 text-gray-700">{row.useCase || '-'}</td>
                    <td className="px-3 py-2 text-gray-700">{row.dataType || '-'}</td>
                    <td className="px-3 py-2 min-w-[240px] max-w-[360px] text-gray-700">{row.description || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.timeline || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{row.budget || '-'}</td>
                    <td className="px-3 py-2">
                      {isAcknowledged ? (
                        <span className="inline-flex rounded bg-green-100 px-2 py-1 text-xs text-green-700 border border-green-200">acknowledged</span>
                      ) : (
                        <select
                          className="bg-white border border-gray-300 rounded px-2 py-1 text-gray-800"
                          value={row.status || 'new'}
                          onChange={(e) => handleStatusChange(row.id, e.target.value)}
                          disabled={savingStatusId === row.id}
                        >
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-2 min-w-[220px]">
                      <textarea
                        className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-gray-800"
                        rows={2}
                        value={notesDrafts[row.id] ?? ''}
                        onChange={(e) => setNotesDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                        onBlur={() => handleNotesBlur(row.id)}
                        disabled={savingNotesId === row.id}
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {isAcknowledged ? (
                        <button
                          type="button"
                          className="px-2 py-1 rounded bg-green-100 text-green-700 border border-green-200 cursor-not-allowed"
                          disabled
                        >
                          ✓ Acknowledge
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                          onClick={() => handleAcknowledge(row.id)}
                          disabled={savingAckId === row.id}
                        >
                          ✓ Acknowledge
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
